
export type TaskStatus = 'pending' | 'in-progress' | 'pending_approval' | 'completed';
export type TaskType = 'one_time' | 'recurring';
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';
export type TaskPriority = 'High' | 'Medium' | 'Low';
export type TaskExtensionStatus = 'NONE' | 'REQUESTED' | 'APPROVED' | 'REJECTED';
export type UserRole = 'owner' | 'manager' | 'staff' | 'super_admin' | 'accountant';

export interface Company {
  id: string;
  name: string;
  subscription_status: string;
  created_at: string;
}

export interface Employee {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  mobile: string;
  company_id: string;
  onesignal_id?: string | null;
  auth_user_id?: string;
  manager_id?: string | null;
}

export interface StaffManagerLink {
  company_id: string;
  staff_id: string;
  manager_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface TaskRemark {
  id: string;
  taskId: string;
  employeeId: string;
  employeeName: string;
  remark: string;
  timestamp: number;
  mentionedUserIds?: string[];
  mentionedDisplayNames?: string[];
}

export type TaskCommentMediaType = 'image' | 'video' | 'file' | 'audio';

export interface TaskComment {
  id: string;
  task_id: string;
  sender_id: string;
  message_text: string;
  created_at: string;
  reply_to_id?: string | null;
  media_url?: string | null;
  media_type?: TaskCommentMediaType | null;
  media_name?: string | null;
  media_size?: number | null;
  is_edited?: boolean;
  edited_at?: string | null;
}

export interface MessageReadReceipt {
  id: string;
  message_id: string;
  reader_id: string;
  read_at: string;
}

export interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
}

export interface TaskParticipant {
  task_id: string;
  user_id: string;
  added_by: string;
  added_at?: string;
  role?: string | null;
  company_id?: string;
}

export interface ProofPhoto {
  imageUrl: string;
  timestamp: number;
}

export type TaskProof = ProofPhoto | ProofPhoto[];

export interface DealershipTask {
  id: string;
  description: string;
  status: TaskStatus;
  priority?: TaskPriority;
  taskType?: TaskType; // One-time vs recurring task type
  recurrenceFrequency?: RecurrenceFrequency | null; // Required when taskType is recurring
  recurrenceTime?: string | null; // HH:MM (24hr) time when recurring task resurfaces
  nextRecurrenceNotificationAt?: number | null; // Next reminder timestamp for recurring task notifications
  extensionStatus?: TaskExtensionStatus;
  requestedDueDate?: number | null;
  createdAt: number;
  deadline?: number; // Timestamp for when the task is due
  completedAt?: number;
  proof?: TaskProof;
  requirePhoto?: boolean; // Whether photo proof is required for completion
  assignedTo?: string; // Employee ID (The person doing the work)
  assignedBy?: string; // Employee ID (The person who created the task)
  parentTaskId?: string; // ID of the master task if this is a sub-task
  company_id: string; // Company ID for multi-tenancy
  remarks?: TaskRemark[]; // Array of progress remarks
  participants?: TaskParticipant[];
  participantUserIds?: string[];
}

export interface FinanceRecord {
  id: string;
  lenderName: string;
  type: 'Trade Advance' | 'Inventory Funding' | 'Working Capital' | 'Other';
  status: 'pending' | 'paid' | 'overdue';
  amount: number;
  dueDate: number; // timestamp
  description?: string;
}

export interface ReceivableRecord {
  id: string;
  sourceName: string;
  category: 'OEM Incentive' | 'Insurance Payout' | 'Finance Payout' | 'Warranty Payout' | 'Other';
  amount: number;
  expectedDate: number; // timestamp
  description?: string;
}

export type DocCategory = 'GST' | 'PAN' | 'Aadhaar' | 'ITR' | 'Trade License' | 'Bank Docs' | 'Other';

export interface DocumentRecord {
  id: string;
  name: string;
  category: DocCategory;
  fileData: string; // Base64
  mimeType: string;
  uploadDate: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

export enum AppTab {
  DASHBOARD = 'dashboard',
  TASKS = 'tasks',
  APPROVALS = 'approvals',
  CRM = 'crm',
  TEAM = 'team',
  FINANCE = 'finance',
  RECEIVABLES = 'receivables',
  DOCUMENTS = 'documents'
}
